import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
    Box, Paper, TableContainer, Table, TableHead, TableRow, TableCell, TableBody,
    TablePagination, TextField, IconButton, Grid, Typography, CircularProgress,
    Snackbar, Alert, Stack, MenuItem, Select, FormControl, InputLabel, Button,
    Chip, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Autocomplete,
    Switch, FormControlLabel
} from "@mui/material";
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    Check as ApproveIcon,
    Close as RejectIcon,
    RestartAlt as ResetIcon,
    SearchOff as NoDataIcon,
    Payments as PaymentsIcon,
    CheckCircleOutline as ApprovedCountIcon,
    Receipt as ReceiptIcon,
    HourglassEmpty as PendingIcon,
    Image as ImageIcon,
    UploadFile as UploadFileIcon,
    Close as CloseIcon,
    Add as AddIcon
} from "@mui/icons-material";
import { getData, postData, putData, deleteData } from "../../../../../services/axiosInstance";
import { API_ENDPOINTS } from "../../../../../services/endpoints";
import StatsCard from "../../../../../components/common/StatsCard";
import { getStatusColor } from "../../../../../utils/statusColor";

function ReimbursementsStepper({ batchData = [], payPeriod }) {
    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    const hrId = userData?.id;
    const fileInputRef = useRef(null);

    const [claims, setClaims] = useState([]);
    const [reimbursementEmployees, setReimbursementEmployees] = useState([]);
    const [upcomingMonths, setUpcomingMonths] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);

    // Search & Filters
    const [searchQuery, setSearchQuery] = useState("");
    const [claimTypeFilter, setClaimTypeFilter] = useState("All");
    const [statusFilter, setStatusFilter] = useState("All");

    // Pagination
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Create / Submit Form State
    const [showCreate, setShowCreate] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [claimType, setClaimType] = useState("");
    const [payrollCycle, setPayrollCycle] = useState(payPeriod || "");
    const [requestedAmount, setRequestedAmount] = useState("");
    const [file, setFile] = useState(null);
    const [filePreview, setFilePreview] = useState(null);

    // Edit / Approve State
    const [openEdit, setOpenEdit] = useState(false);
    const [selectedClaim, setSelectedClaim] = useState(null);
    const [approvedAmount, setApprovedAmount] = useState("");
    const [editPayrollCycle, setEditPayrollCycle] = useState("");
    const [comment, setComment] = useState("");

    // Proof Preview State
    const [openPreview, setOpenPreview] = useState(false);
    const [previewUrl, setPreviewUrl] = useState("");
    const [previewType, setPreviewType] = useState("");

    // Confirm dialog
    const [confirmMsg, setConfirmMsg] = useState(null);
    const [showNextFY, setShowNextFY] = useState(false);

    // Feedback
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

    // Fetch Employees for dropdown
    const fetchEmployees = useCallback(async () => {
        if (!hrId) return;
        try {
            const res = await getData(API_ENDPOINTS.getPayrollsByHrId(hrId));
            const data = res?.result || [];
            const filtered = data.filter((emp) => emp.id !== null);
            setReimbursementEmployees(filtered);
        } catch (err) {
            setReimbursementEmployees([]);
        }
    }, [hrId]);

    // Fetch Upcoming Payroll Months
    const fetchUpcomingMonths = useCallback(async (includeNext = false) => {
        try {
            const res = await getData(API_ENDPOINTS.getUpcomingPayrollMonths(includeNext));
            if (res?.statusCode === 200) {
                const formatted = res.result.months.map((m) => {
                    const [y, mo] = m.split("-");
                    const names = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
                    return `${names[Number(mo) - 1]} ${y}`;
                });
                setUpcomingMonths(formatted);
            }
        } catch (err) {
            setUpcomingMonths([]);
        }
    }, []);

    // Fetch Reimbursement Claims
    const fetchReimbursements = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getData(API_ENDPOINTS.getReimbursementsByHrId(hrId));
            if (res?.statusCode === 200 && Array.isArray(res.result)) {
                const flat = [];
                res.result.forEach((group) => {
                    if (Array.isArray(group.reimbursements)) {
                        group.reimbursements.forEach((item) => {
                            const emp = item.employee || {};
                            flat.push({
                                id: item.id,
                                employeeId: emp.emp_id || `EMP-${item.employeePersonalId}`,
                                employeeName: emp.first_name ? `${emp.first_name} ${emp.last_name || ""}` : item.employeeName || "Employee",
                                claimType: item.claimType,
                                claimDate: item.createdAt ? new Date(item.createdAt).toLocaleDateString() : "-",
                                requestedAmount: Number(item.postamount || 0),
                                approvedAmount: Number(item.amount || 0),
                                receiptUrl: item.receiptUrl,
                                status: Boolean(item.status),
                                payrollCycle: item.payrollCycle || group.payrollCycle || "Not Assigned"
                            });
                        });
                    }
                });
                setClaims(flat);
            } else {
                setClaims([]);
            }
        } catch (error) {
            setClaims([]);
        } finally {
            setLoading(false);
        }
    }, [hrId]);

    useEffect(() => {
        fetchEmployees();
        fetchUpcomingMonths(showNextFY);
        fetchReimbursements();
    }, [fetchEmployees, fetchUpcomingMonths, fetchReimbursements, showNextFY]);

    useEffect(() => {
        if (payPeriod) {
            setPayrollCycle(payPeriod);
        }
    }, [payPeriod]);

    // --- Summary Metrics ---
    const summaryMetrics = useMemo(() => {
        const totalClaims = claims.length;
        let totalRequested = 0;
        let totalApproved = 0;
        let pendingCount = 0;
        let approvedCount = 0;

        claims.forEach((item) => {
            totalRequested += Number(item.requestedAmount || 0);
            totalApproved += Number(item.approvedAmount || 0);
            if (item.status) {
                approvedCount += 1;
            } else {
                pendingCount += 1;
            }
        });

        return { totalClaims, totalRequested, totalApproved, pendingCount, approvedCount };
    }, [claims]);

    // --- Filter & Search ---
    const filteredClaims = useMemo(() => {
        return claims.filter((claim) => {
            const matchesSearch =
                !searchQuery ||
                claim.employeeId?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                claim.employeeName?.toLowerCase().includes(searchQuery.toLowerCase());

            const matchesClaimType =
                claimTypeFilter === "All" || claim.claimType === claimTypeFilter;

            const matchesStatus =
                statusFilter === "All" ||
                (statusFilter === "Approved" && claim.status) ||
                (statusFilter === "Pending" && !claim.status);

            return matchesSearch && matchesClaimType && matchesStatus;
        });
    }, [claims, searchQuery, claimTypeFilter, statusFilter]);

    // --- File Handling ---
    const MAX_FILE_SIZE_MB = 20;

    const handleFileChange = (e) => {
        const f = e.target.files[0];
        if (!f) return;

        if (f.type !== "application/pdf") {
            setSnackbar({ open: true, message: "Only PDF is allowed", severity: "error" });
            if (fileInputRef.current) fileInputRef.current.value = "";
            return;
        }

        if (f.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
            setSnackbar({ open: true, message: `Size exceeds ${MAX_FILE_SIZE_MB}MB`, severity: "error" });
            if (fileInputRef.current) fileInputRef.current.value = "";
            return;
        }

        setFile(f);
        setFilePreview(URL.createObjectURL(f));
    };

    const removeFile = () => {
        setFile(null);
        setFilePreview(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    // --- Create Claim ---
    const handleCreate = async () => {
        if (!selectedEmployee || !claimType || !payrollCycle || !requestedAmount || !file) {
            setSnackbar({ open: true, message: "Please fill all required fields", severity: "error" });
            return;
        }

        setSubmitLoading(true);
        try {
            const fd = new FormData();
            fd.append("postamount", requestedAmount);
            fd.append("amount", requestedAmount);
            fd.append("employeePersonalId", selectedEmployee.employeePersonalId || selectedEmployee.id);
            fd.append("employeeName", `${selectedEmployee.first_name || ""} ${selectedEmployee.last_name || ""}`.trim());
            fd.append("claimType", claimType);
            fd.append("payrollCycle", payrollCycle);
            fd.append("status", false);
            fd.append("hr_id", hrId);
            fd.append("receipt", file);

            const res = await postData(API_ENDPOINTS.createReimbursement, fd);
            if (res?.statusCode === 200) {
                setSnackbar({ open: true, message: "Claim submitted successfully", severity: "success" });
                setShowCreate(false);
                setSelectedEmployee(null);
                setClaimType("");
                setRequestedAmount("");
                removeFile();
                fetchReimbursements();
            }
        } catch (error) {
            setSnackbar({ open: true, message: "Failed to submit claim", severity: "error" });
        } finally {
            setSubmitLoading(false);
        }
    };

    // --- Edit / Approve Claim ---
    const openEditDialog = (row) => {
        setSelectedClaim(row);
        setApprovedAmount(row.approvedAmount || row.requestedAmount || "");
        setEditPayrollCycle(row.payrollCycle !== "Not Assigned" ? row.payrollCycle : payPeriod || "");
        setComment("");
        setOpenEdit(true);
    };

    const handleApprove = async (isFinalApprove = true) => {
        if (!selectedClaim) return;
        if (Number(approvedAmount) < 0 || Number(approvedAmount) > selectedClaim.requestedAmount) {
            setSnackbar({ open: true, message: "Approved amount must be between 0 and requested amount", severity: "error" });
            return;
        }
        if (!editPayrollCycle) {
            setSnackbar({ open: true, message: "Please select a Payroll Cycle", severity: "error" });
            return;
        }

        const payload = {
            id: selectedClaim.id,
            comment,
            status: isFinalApprove,
            amount: Number(approvedAmount) || 0,
            payrollCycle: editPayrollCycle,
        };

        try {
            setSubmitLoading(true);
            const res = await putData(API_ENDPOINTS.updateReimbursementById, payload);
            if (res?.statusCode === 200) {
                setSnackbar({
                    open: true,
                    message: isFinalApprove ? "Claim approved and assigned to payroll" : "Claim details updated",
                    severity: "success"
                });
                setOpenEdit(false);
                fetchReimbursements();
            }
        } catch (error) {
            setSnackbar({ open: true, message: "Error updating claim status", severity: "error" });
        } finally {
            setSubmitLoading(false);
        }
    };

    // --- Delete Claim ---
    const handleDelete = async (row) => {
        if (row.status) {
            setSnackbar({ open: true, message: "Cannot delete approved claim", severity: "warning" });
            return;
        }
        try {
            await deleteData(API_ENDPOINTS.deleteReimbursementById(row.id));
            setSnackbar({ open: true, message: "Claim deleted successfully", severity: "success" });
            fetchReimbursements();
        } catch (error) {
            setSnackbar({ open: true, message: "Error deleting claim", severity: "error" });
        }
    };

    const handleReset = () => {
        setSearchQuery("");
        setClaimTypeFilter("All");
        setStatusFilter("All");
        setPage(0);
    };

    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>;
    }

    return (
        <Box>
            {/* Header & Submit Action */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                <Box>
                    <Typography variant="h6" fontWeight="bold">Reimbursements Processing</Typography>
                    <Typography variant="body2" color="textSecondary">
                        Review and approve employee reimbursement claims for {payPeriod}
                    </Typography>
                </Box>
                <FormControlLabel
                    control={
                        <Switch
                            checked={showCreate}
                            onChange={(e) => setShowCreate(e.target.checked)}
                            color="primary"
                        />
                    }
                    label="Submit Claim on Behalf"
                    componentsProps={{ typography: { variant: 'body2', fontWeight: 600 } }}
                />
            </Stack>

            {/* Summary Metrics Cards */}
            <Grid container spacing={2} mb={3}>
                <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatsCard
                        title="Total Claims"
                        value={summaryMetrics.totalClaims}
                        icon={<ReceiptIcon />}
                        color="#6366F1"
                    />
                </Grid>
                <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatsCard
                        title="Total Requested"
                        value={`₹${summaryMetrics.totalRequested.toLocaleString()}`}
                        icon={<PaymentsIcon />}
                        color="#F59E0B"
                    />
                </Grid>
                <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatsCard
                        title="Total Approved"
                        value={`₹${summaryMetrics.totalApproved.toLocaleString()}`}
                        icon={<ApprovedCountIcon />}
                        color="#10B981"
                    />
                </Grid>
                <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatsCard
                        title="Pending Approval"
                        value={summaryMetrics.pendingCount}
                        icon={<PendingIcon />}
                        color="#3B82F6"
                    />
                </Grid>
            </Grid>

            {/* Form for Submitting Claim on Behalf */}
            {showCreate && (
                <Paper variant="outlined" sx={{ p: 3, mb: 4, borderRadius: 3, bgcolor: '#F8FAFC' }}>
                    <Typography variant="subtitle1" fontWeight="bold" mb={2}>
                        Submit Reimbursement Claim for Employee
                    </Typography>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item size={{ xs: 12, md: 6 }}>
                            <Autocomplete
                                options={reimbursementEmployees}
                                value={selectedEmployee}
                                onChange={(e, v) => setSelectedEmployee(v)}
                                getOptionLabel={(o) => `${o.first_name || ""} ${o.last_name || ""} (${o.emp_id || o.employeeCode})`}
                                size="small"
                                renderInput={(params) => <TextField {...params} label="Select Employee" size="small" />}
                                filterSelectedOptions
                            />
                        </Grid>
                        <Grid item size={{ xs: 12, md: 6 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel id="claim-type-label">Claim Type</InputLabel>
                                <Select
                                    labelId="claim-type-label"
                                    label="Claim Type"
                                    value={claimType}
                                    onChange={(e) => setClaimType(e.target.value)}
                                >
                                    <MenuItem value="Medical">Medical</MenuItem>
                                    <MenuItem value="Travel">Travel</MenuItem>
                                    <MenuItem value="Internet">Internet</MenuItem>
                                    <MenuItem value="Fuel">Fuel</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item size={{ xs: 12, md: 6 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel id="payroll-cycle-label">Payroll Cycle</InputLabel>
                                <Select
                                    labelId="payroll-cycle-label"
                                    label="Payroll Cycle"
                                    value={payrollCycle}
                                    onChange={(e) => {
                                        if (e.target.value === "ADD_NEXT_FY") {
                                            setConfirmMsg("This will load months for the next financial year. Do you want to proceed?");
                                        } else {
                                            setPayrollCycle(e.target.value);
                                        }
                                    }}
                                >
                                    {upcomingMonths.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                                    {!showNextFY && (
                                        <MenuItem value="ADD_NEXT_FY" sx={{ color: 'primary.main', fontWeight: 600 }}>
                                            + Add Next Financial Year
                                        </MenuItem>
                                    )}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item size={{ xs: 12, md: 6 }}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Requested Amount (₹)"
                                type="number"
                                value={requestedAmount}
                                onChange={(e) => setRequestedAmount(e.target.value)}
                                inputProps={{ min: 0 }}
                            />
                        </Grid>
                        <Grid item size={{ xs: 12 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Button component="label" variant="outlined" startIcon={<UploadFileIcon />} sx={{ textTransform: 'none' }}>
                                    Upload Receipt (PDF)
                                    <input hidden type="file" accept="application/pdf" ref={fileInputRef} onChange={handleFileChange} />
                                </Button>
                                {file && <Typography variant="body2" color="success.main">{file.name}</Typography>}
                                <Typography variant="caption" color="text.secondary">PDF Only · Max {MAX_FILE_SIZE_MB}MB</Typography>
                            </Box>
                        </Grid>
                        {filePreview && (
                            <Grid item size={{ xs: 12 }}>
                                <Paper sx={{ width: 240, p: 1, position: "relative" }}>
                                    <IconButton size="small" sx={{ position: "absolute", top: 2, right: 2 }} onClick={removeFile}><CloseIcon fontSize="small" /></IconButton>
                                    <embed src={filePreview} type="application/pdf" width="100%" />
                                </Paper>
                            </Grid>
                        )}
                        <Grid item size={{ xs: 12 }}>
                            <Button variant="contained" onClick={handleCreate} disabled={submitLoading} startIcon={<AddIcon />}>
                                {submitLoading ? <CircularProgress size={20} /> : "Submit Claim"}
                            </Button>
                        </Grid>
                    </Grid>
                </Paper>
            )}

            {/* Filters */}
            <Grid container spacing={2} mb={3}>
                <Grid item size={{ xs: 12, md: 5 }}>
                    <TextField
                        size="small"
                        fullWidth
                        placeholder="Search employee by ID or name"
                        value={searchQuery}
                        onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
                    />
                </Grid>
                <Grid item size={{ xs: 6, md: 2.5 }}>
                    <FormControl size="small" fullWidth>
                        <InputLabel>Claim Type</InputLabel>
                        <Select
                            label="Claim Type"
                            value={claimTypeFilter}
                            onChange={(e) => { setClaimTypeFilter(e.target.value); setPage(0); }}
                        >
                            <MenuItem value="All">All Types</MenuItem>
                            <MenuItem value="Medical">Medical</MenuItem>
                            <MenuItem value="Travel">Travel</MenuItem>
                            <MenuItem value="Internet">Internet</MenuItem>
                            <MenuItem value="Fuel">Fuel</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item size={{ xs: 6, md: 2.5 }}>
                    <FormControl size="small" fullWidth>
                        <InputLabel>Status</InputLabel>
                        <Select
                            label="Status"
                            value={statusFilter}
                            onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
                        >
                            <MenuItem value="All">All Statuses</MenuItem>
                            <MenuItem value="Approved">Approved</MenuItem>
                            <MenuItem value="Pending">Pending</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item size={{ xs: 12, md: 2 }}>
                    <Button fullWidth startIcon={<ResetIcon />} onClick={handleReset} sx={{ textTransform: 'none', color: 'text.secondary' }}>Reset</Button>
                </Grid>
            </Grid>

            {/* Claims Table */}
            <TableContainer component={Paper} sx={{ borderRadius: '12px', border: '1px solid #E2E8F0', boxShadow: 'none' }}>
                <Table sx={{ minWidth: 900 }}>
                    <TableHead sx={{ bgcolor: '#EFF6FF' }}>
                        <TableRow>
                            <TableCell sx={{ color: '#1D4ED8', fontWeight: 700 }}>Employee ID</TableCell>
                            <TableCell sx={{ color: '#1D4ED8', fontWeight: 700 }}>Employee Name</TableCell>
                            <TableCell sx={{ color: '#1D4ED8', fontWeight: 700 }}>Claim Type</TableCell>
                            <TableCell sx={{ color: '#1D4ED8', fontWeight: 700 }}>Claim Date</TableCell>
                            <TableCell sx={{ color: '#1D4ED8', fontWeight: 700 }}>Cycle</TableCell>
                            <TableCell align="right" sx={{ color: '#1D4ED8', fontWeight: 700 }}>Requested</TableCell>
                            <TableCell align="right" sx={{ color: '#1D4ED8', fontWeight: 700 }}>Approved</TableCell>
                            <TableCell align="center" sx={{ color: '#1D4ED8', fontWeight: 700 }}>Status</TableCell>
                            <TableCell align="center" sx={{ color: '#1D4ED8', fontWeight: 700 }}>Proof</TableCell>
                            <TableCell align="center" sx={{ color: '#1D4ED8', fontWeight: 700 }}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody sx={{ bgcolor: '#FFFFFF' }}>
                        {filteredClaims.length > 0 ? (
                            filteredClaims.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => {
                                const statusKey = row.status ? "Approved" : "Pending";
                                const colors = getStatusColor(statusKey);

                                return (
                                    <TableRow key={row.id} hover sx={{ '&:hover': { bgcolor: '#F1F5F9 !important' } }}>
                                        <TableCell sx={{ fontWeight: 600 }}>{row.employeeId}</TableCell>
                                        <TableCell>{row.employeeName}</TableCell>
                                        <TableCell>{row.claimType}</TableCell>
                                        <TableCell>{row.claimDate}</TableCell>
                                        <TableCell>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontWeight: row.payrollCycle === "Not Assigned" ? 400 : 700,
                                                    color: row.payrollCycle === "Not Assigned" ? "orange" : "primary.main",
                                                    fontStyle: row.payrollCycle === "Not Assigned" ? "italic" : "normal"
                                                }}
                                            >
                                                {row.payrollCycle}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">₹{row.requestedAmount?.toLocaleString()}</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700, color: row.approvedAmount > 0 ? 'success.main' : 'inherit' }}>
                                            ₹{row.approvedAmount?.toLocaleString()}
                                        </TableCell>
                                        <TableCell align="center">
                                            <Box
                                                sx={{
                                                    display: "inline-block",
                                                    px: 1.5,
                                                    py: 0.5,
                                                    borderRadius: 10,
                                                    fontSize: "0.75rem",
                                                    fontWeight: "bold",
                                                    bgcolor: colors.bg,
                                                    color: colors.color,
                                                }}
                                            >
                                                {statusKey}
                                            </Box>
                                        </TableCell>
                                        <TableCell align="center">
                                            {row.receiptUrl ? (
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={() => {
                                                        setPreviewUrl(row.receiptUrl);
                                                        setPreviewType(row.receiptUrl.toLowerCase().endsWith(".pdf") ? "pdf" : "img");
                                                        setOpenPreview(true);
                                                    }}
                                                >
                                                    <ImageIcon fontSize="small" />
                                                </IconButton>
                                            ) : (
                                                <Typography variant="caption" color="text.secondary">-</Typography>
                                            )}
                                        </TableCell>
                                        <TableCell align="center">
                                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                                <Tooltip title={row.status ? "Edit / Re-verify" : "Approve / Edit"}>
                                                    <IconButton size="small" color="primary" onClick={() => openEditDialog(row)}>
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title={row.status ? "Cannot delete approved claim" : "Delete"}>
                                                    <span>
                                                        <IconButton
                                                            disabled={row.status}
                                                            onClick={() => handleDelete(row)}
                                                            size="small"
                                                            color="error"
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        ) : (
                            <TableRow>
                                <TableCell colSpan={10} align="center" sx={{ py: 10 }}>
                                    <NoDataIcon sx={{ fontSize: 60, color: '#CBD5E1', mb: 1 }} />
                                    <Typography variant="h6" color="textSecondary">No Reimbursement Claims Found</Typography>
                                    <Typography variant="body2" color="textSecondary">Try adjusting search filters or submit a new claim</Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <TablePagination
                component="div"
                count={filteredClaims.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25]}
            />

            {/* APPROVE / EDIT DIALOG */}
            <Dialog open={openEdit} onClose={() => setOpenEdit(false)} fullWidth maxWidth="sm">
                <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: 'center', bgcolor: 'primary.main', color: 'white', py: 1.5 }}>
                    <Typography variant="h6" fontWeight="bold">Verify & Assign Reimbursement</Typography>
                    <IconButton onClick={() => setOpenEdit(false)} sx={{ color: 'white' }}><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {selectedClaim && (
                        <Stack spacing={2} sx={{ mt: 1 }}>
                            <Box>
                                <Typography variant="body1" fontWeight="bold">
                                    {selectedClaim.employeeName} ({selectedClaim.employeeId})
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Type: {selectedClaim.claimType} | Date: {selectedClaim.claimDate}
                                </Typography>
                            </Box>
                            <TextField
                                label="Requested Amount (₹)"
                                value={`₹ ${selectedClaim.requestedAmount?.toLocaleString() || 0}`}
                                InputProps={{ readOnly: true }}
                                fullWidth
                                size="small"
                                sx={{ backgroundColor: "#F8FAFC" }}
                            />
                            <TextField
                                label="Approved Amount (₹)"
                                type="number"
                                size="small"
                                value={approvedAmount}
                                onChange={(e) => setApprovedAmount(e.target.value)}
                                fullWidth
                                error={Number(approvedAmount) > (selectedClaim.requestedAmount || 0)}
                                helperText={Number(approvedAmount) > (selectedClaim.requestedAmount || 0) ? `Amount cannot exceed requested ₹${selectedClaim.requestedAmount}` : ""}
                            />
                            <FormControl fullWidth size="small">
                                <InputLabel>Payroll Cycle (Payout Period)</InputLabel>
                                <Select
                                    value={editPayrollCycle}
                                    onChange={(e) => {
                                        if (e.target.value === "ADD_NEXT_FY") {
                                            setConfirmMsg("This will load months for the next financial year. Do you want to proceed?");
                                        } else {
                                            setEditPayrollCycle(e.target.value);
                                        }
                                    }}
                                    label="Payroll Cycle (Payout Period)"
                                >
                                    {upcomingMonths.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                                    {!showNextFY && (
                                        <MenuItem value="ADD_NEXT_FY" sx={{ color: 'primary.main', fontWeight: 600 }}>+ Add Next Financial Year</MenuItem>
                                    )}
                                </Select>
                            </FormControl>
                            <TextField
                                label="Comment / Note"
                                multiline
                                rows={2}
                                size="small"
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                                fullWidth
                            />
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2, bgcolor: '#F8FAFC' }}>
                    <Button variant="outlined" startIcon={<RejectIcon />} onClick={() => setOpenEdit(false)}>Cancel</Button>
                    <Button
                        variant="outlined"
                        onClick={() => handleApprove(false)}
                        disabled={submitLoading || !editPayrollCycle}
                    >
                        Save Draft
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<ApproveIcon />}
                        onClick={() => handleApprove(true)}
                        disabled={submitLoading || Number(approvedAmount) < 0 || Number(approvedAmount) > (selectedClaim?.requestedAmount || 0) || !editPayrollCycle}
                    >
                        {submitLoading ? <CircularProgress size={20} /> : "Approve & Payout"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* RECEIPT PREVIEW DIALOG */}
            <Dialog open={openPreview} onClose={() => setOpenPreview(false)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: 'center' }}>
                    Receipt Preview
                    <IconButton onClick={() => setOpenPreview(false)}><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent>
                    {previewType === "pdf" ? (
                        <embed src={previewUrl} type="application/pdf" width="100%" height="500px" />
                    ) : (
                        <img src={previewUrl} alt="receipt" style={{ width: "100%", maxHeight: "500px", objectFit: "contain" }} />
                    )}
                </DialogContent>
            </Dialog>

            {/* FEEDBACK SNACKBAR */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity={snackbar.severity} variant="filled" sx={{ width: '100%', borderRadius: 2 }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>

            {/* CONFIRM DIALOG FOR NEXT FY */}
            <Dialog open={!!confirmMsg} onClose={() => setConfirmMsg(null)}>
                <DialogTitle>Confirm Action</DialogTitle>
                <DialogContent><Typography>{confirmMsg}</Typography></DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmMsg(null)}>Cancel</Button>
                    <Button variant="contained" onClick={() => { setShowNextFY(true); setConfirmMsg(null); }}>Load Next FY</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default ReimbursementsStepper;
